
import { useEffect, useRef, useState } from 'react';

export default function EmpathySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const painPoints = [
    {
      icon: '😰',
      text: 'プログラミング教室、どこも難しそうで尻込みしていませんか？',
      delay: '0s'
    },
    {
      icon: '😓',
      text: '自分が教えられないから不安',
      delay: '0.2s'
    },
    {
      icon: '🤔',
      text: 'エンジニアを目指すわけじゃないけど、基礎は身につけてほしい',
      delay: '0.4s'
    },
    {
      icon: '💸',
      text: 'いきなり高いパソコンを買うのは勇気がいる',
      delay: '0.6s'
    }
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-yellow-50 to-pink-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-pink-200 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block bg-pink-100 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6">
            <span className="text-pink-600 font-bold text-xs sm:text-sm">こんなお悩み、ありませんか？</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-4 sm:mb-6 px-2 leading-tight">
            プログラミング教室、<br />
            <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              どこも難しそうで<br className="sm:hidden" />尻込みしていませんか？
            </span>
          </h2>
        </div>

        {/* Pain Points Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-20">
          {painPoints.map((point, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: point.delay }}
            >
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-5xl flex-shrink-0 animate-bounce" style={{ animationDuration: '2s', animationDelay: point.delay }}>
                  {point.icon}
                </div>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 font-medium leading-relaxed break-words">
                  {point.text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Story Section */}
        <div className={`bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl transform transition-all duration-1000 delay-700 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <div className="text-center mb-6 sm:mb-10">
            <div className="inline-block bg-white px-4 sm:px-8 py-2 sm:py-3 rounded-full shadow-md mb-4 sm:mb-6">
              <span className="text-base sm:text-2xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                そんなあなたに、<br className="sm:hidden" />お伝えしたいことがあります
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-10 items-center">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/c8002ecac1353f802e664ffdf287e2af.jpg"
                alt="普通のパパ"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full"
              />
            </div>
            <div className="space-y-4 sm:space-y-6">
              <h3 className="text-xl sm:text-3xl md:text-4xl font-black text-gray-800 mb-4 sm:mb-6 leading-tight">
                「開発者は、<br />
                <span className="text-pink-500">普通のパパ</span>でした。」
              </h3>
              <div className="space-y-3 sm:space-y-4 text-gray-700 text-sm sm:text-lg leading-relaxed">
                <p className="flex items-start space-x-2 sm:space-x-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">💡</span>
                  <span className="break-words">なぜ非エンジニアの父がこれを作ったのか？</span>
                </p>
                <p className="flex items-start space-x-2 sm:space-x-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">✨</span>
                  <span className="break-words">難しい専門用語を排除し、子供が「楽しい！」と言った言葉だけで作った教材。</span>
                </p>
                <p className="flex items-start space-x-2 sm:space-x-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">❤️</span>
                  <span className="break-words">「娘が夢中になった」という実体験に基づいた、他にはない安心感。</span>
                </p>
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg mt-6 sm:mt-8">
                <p className="text-base sm:text-xl font-bold text-center text-gray-800 break-words">
                  「娘の笑顔が、<br className="sm:hidden" />すべての答えでした」
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
